import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Container, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Navbar from '../../../Navbar';
import PageTitle from '../../../components/PageTitle';
import {
  authHeaders,
  flashcardsFetch,
  shuffleArray,
  stripHtmlToText
} from './flashcardsUtils';
import '../guides.css';

/** Ensure every card in the session has a result; unmarked → unknown. */
function finalizeReviewResults(queue, results, currentId, currentResult) {
  const byId = new Map(results.map((r) => [r.cardId, r]));
  if (currentId != null && currentResult) {
    byId.set(currentId, { cardId: currentId, result: currentResult });
  }
  return queue.map((card) => byId.get(card.id) || { cardId: card.id, result: 'unknown' });
}

function resultForCard(results, cardId) {
  return results.find((r) => r.cardId === cardId)?.result || null;
}

function isPositiveResult(result) {
  return result === 'known' || result === 'correct';
}

function scoreHeadline(score, mode) {
  if (score >= 100) {
    return {
      title: 'Mastered',
      blurb: mode === 'test'
        ? 'Every card correct. That set is done.'
        : 'Every term, cold. That set is done.'
    };
  }
  if (score >= 80) {
    return {
      title: 'Strong work',
      blurb: 'You\'re close — a quick pass on the rest will lock it in.'
    };
  }
  if (score >= 50) {
    return {
      title: 'Getting there',
      blurb: 'Solid progress. Review the ones still learning.'
    };
  }
  return {
    title: 'Keep practicing',
    blurb: 'Another round will help these stick.'
  };
}

function cardTerm(card) {
  const text = stripHtmlToText(card?.frontHtml);
  if (!text) return 'Untitled card';
  return text.length > 64 ? `${text.slice(0, 61)}…` : text;
}

function statusLabel(result, mode) {
  if (result === 'correct') return 'Correct';
  if (result === 'incorrect') return 'Wrong';
  if (result === 'known') return 'Known';
  if (result === 'unknown') return mode === 'test' ? 'Wrong' : 'Still learning';
  return mode === 'test' ? 'Wrong' : 'Still learning';
}

export default function FlashcardStudyPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const browse = searchParams.get('view') === 'browse';
  const mode = searchParams.get('mode') === 'test' ? 'test' : 'review';
  const filter = ['all', 'correct', 'wrong'].includes(searchParams.get('filter'))
    ? searchParams.get('filter')
    : 'all';
  const shuffled = !browse && searchParams.get('shuffled') === '1';
  const [layout, setLayout] = useState('cards');
  const [revealedIds, setRevealedIds] = useState(() => new Set());

  const [setData, setSetData] = useState(null);
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finished, setFinished] = useState(false);
  const [saving, setSaving] = useState(false);
  const [startedAt, setStartedAt] = useState(() => new Date().toISOString());
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await flashcardsFetch(`/sets/${id}`, { headers: authHeaders() });
      if (!browse && !(data.canStudy ?? data.canEdit)) {
        navigate(`/guides/flashcards/sets/${id}/study?view=browse`, { replace: true });
        return;
      }
      setSetData(data);
      let cards = [...(data.cards || [])].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      if (!browse && filter === 'correct') {
        const ids = new Set(data.correctCardIds || []);
        cards = cards.filter((c) => ids.has(c.id));
      } else if (!browse && filter === 'wrong') {
        const ids = new Set(data.wrongCardIds || []);
        cards = cards.filter((c) => ids.has(c.id));
      }
      if (shuffled) cards = shuffleArray(cards);
      if (!cards.length) {
        toast.info('No cards match this filter');
        navigate(`/guides/flashcards/sets/${id}`, { replace: true });
        return;
      }
      setQueue(cards);
      setIndex(0);
      setFlipped(false);
      setRevealedIds(new Set());
      setResults([]);
      setFinished(false);
      setSummary(null);
      setStartedAt(new Date().toISOString());
    } catch (err) {
      toast.error(err.message || 'Failed to start session');
      navigate(`/guides/flashcards/sets/${id}`);
    } finally {
      setLoading(false);
    }
  }, [id, filter, shuffled, browse, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const current = queue[index] || null;
  const currentResult = current ? resultForCard(results, current.id) : null;
  const progressLabel = useMemo(
    () => (queue.length ? `${index + 1} of ${queue.length}` : ''),
    [index, queue.length]
  );

  const sessionLabel = browse ? 'Cards' : mode === 'test' ? 'Test' : 'Review';
  const allRevealed = browse && queue.length > 0 && queue.every((card) => revealedIds.has(card.id));

  const toggleReveal = (cardId) => {
    setRevealedIds((prev) => {
      const next = new Set(prev);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return next;
    });
  };

  const toggleRevealAll = () => {
    if (allRevealed) {
      setRevealedIds(new Set());
      return;
    }
    setRevealedIds(new Set(queue.map((card) => card.id)));
  };
  const segmentStates = useMemo(
    () => queue.map((card, i) => {
      if (i === index) return 'current';
      const result = resultForCard(results, card.id);
      if (!result) return 'pending';
      return isPositiveResult(result) ? 'pos' : 'neg';
    }),
    [queue, index, results]
  );

  const negativeLabel = mode === 'test' ? 'Wrong' : 'Unknown';
  const positiveLabel = mode === 'test' ? 'Correct' : 'Known';
  const negativeResult = mode === 'test' ? 'incorrect' : 'unknown';
  const positiveResult = mode === 'test' ? 'correct' : 'known';
  const positiveStatLabel = mode === 'test' ? 'Correct' : 'Known';
  const negativeStatLabel = mode === 'test' ? 'Wrong' : 'Still learning';

  const saveAttempt = async (finalResults) => {
    setFinished(true);
    setSaving(true);
    try {
      const attempt = await flashcardsFetch(`/sets/${id}/attempts`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          mode,
          filter,
          shuffled,
          startedAt,
          results: finalResults
        })
      });
      setSummary(attempt);
      setResults(finalResults);
    } catch (err) {
      toast.error(err.message || 'Failed to save attempt');
    } finally {
      setSaving(false);
    }
  };

  const upsertResult = (cardId, result, base = results) => (
    [...base.filter((r) => r.cardId !== cardId), { cardId, result }]
  );

  const finishSession = async (nextResults) => {
    if (mode === 'review') {
      await saveAttempt(finalizeReviewResults(queue, nextResults));
      return;
    }
    const missing = queue.filter((c) => !nextResults.some((r) => r.cardId === c.id));
    if (missing.length) {
      toast.info('Mark Correct or Wrong on every card before finishing');
      return;
    }
    await saveAttempt(nextResults);
  };

  const markAndAdvance = async (result) => {
    if (!current || finished || !flipped) return;
    const nextResults = upsertResult(current.id, result);

    if (index + 1 >= queue.length) {
      await finishSession(nextResults);
      return;
    }

    setResults(nextResults);
    setIndex((i) => i + 1);
    setFlipped(false);
  };

  const goPrev = () => {
    if (index <= 0 || finished) return;
    setIndex((i) => i - 1);
    setFlipped(false);
  };

  const goNext = async () => {
    if (!current || finished) return;
    if (browse) {
      if (index + 1 >= queue.length) return;
      setIndex((i) => i + 1);
      setFlipped(false);
      return;
    }

    // Leaving a card without marking → unknown in review
    let nextResults = results;
    if (mode === 'review' && !resultForCard(results, current.id)) {
      nextResults = upsertResult(current.id, 'unknown');
      setResults(nextResults);
    }

    if (index + 1 >= queue.length) {
      await finishSession(nextResults);
      return;
    }

    setIndex((i) => i + 1);
    setFlipped(false);
  };

  if (loading || !setData) {
    return (
      <div className="guides-app guides-loading-screen">
        <Navbar pageTitle="Study" />
        <div className="guides-loading"><Spinner /></div>
      </div>
    );
  }

  const deckId = setData.section?.deck?.id;
  const leaveTarget = deckId
    ? `/guides/flashcards/decks/${deckId}`
    : (setData.canStudy || setData.canEdit)
      ? `/guides/flashcards/sets/${id}`
      : '/guides/discover';

  if (finished) {
    const knownCount = summary?.correctCount != null
      ? Number(summary.correctCount)
      : results.filter((r) => isPositiveResult(r.result)).length;
    const unknownCount = summary?.incorrectCount != null
      ? Number(summary.incorrectCount)
      : results.filter((r) => !isPositiveResult(r.result)).length;
    const totalMarked = knownCount + unknownCount;
    const score = summary?.scorePercent != null
      ? Number(summary.scorePercent)
      : (totalMarked > 0 ? Math.round((knownCount / totalMarked) * 1000) / 10 : 0);
    const headline = scoreHeadline(score, mode);
    const scoreDisplay = Number.isInteger(score) ? String(score) : score.toFixed(1);

    return (
      <div className="guides-app guides-topic-editor-page bg-inverse">
        <Navbar pageTitle={setData.title} />
        <PageTitle title="Session complete" />
        <Container fluid="lg" className="py-4 py-md-5">
          <div className="fc-results-card">
            {saving ? (
              <div className="guides-loading"><Spinner /></div>
            ) : (
              <>
                <div className="fc-results-hero">
                  <div className="fc-results-score-badge" aria-label={`Score ${scoreDisplay}%`}>
                    <span className="fc-results-score-value">{scoreDisplay}%</span>
                  </div>
                  <h2 className="fc-results-title">{headline.title}</h2>
                  <p className="fc-results-blurb">{headline.blurb}</p>
                </div>

                <div className="fc-results-stats">
                  <div className="fc-results-stat">
                    <div className="fc-results-stat-value is-pos">{knownCount}</div>
                    <div className="fc-results-stat-label">{positiveStatLabel}</div>
                  </div>
                  <div className="fc-results-stat">
                    <div className="fc-results-stat-value is-neg">{unknownCount}</div>
                    <div className="fc-results-stat-label">{negativeStatLabel}</div>
                  </div>
                </div>

                <div className="fc-results-divider" />

                <ul className="fc-results-list">
                  {queue.map((card) => {
                    const result = resultForCard(results, card.id);
                    const positive = isPositiveResult(result);
                    return (
                      <li key={card.id} className="fc-results-row">
                        <span className="fc-results-term">{cardTerm(card)}</span>
                        <span className={`fc-results-status${positive ? ' is-pos' : ' is-neg'}`}>
                          {statusLabel(result, mode)}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <div className="fc-results-actions">
                  <Button
                    variant="primary"
                    className="fc-results-again-btn"
                    onClick={() => load()}
                  >
                    Study again
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => navigate(leaveTarget)}
                  >
                    {setData.section?.deck?.id ? 'Back to deck' : 'Back to set'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="guides-app guides-topic-editor-page bg-inverse">
      <Navbar pageTitle={setData.title} />
      <PageTitle title={browse ? `Cards · ${setData.title}` : `${mode === 'test' ? 'Test' : 'Review'} · ${setData.title}`} />

      <Container fluid="lg" className="guides-topic-editor-workspace">
        <div className="guides-read-scroll pt-3 pe-0">
          <div className="guides-list-header">
            <div className="min-w-0">
              <h6 className="mb-0 fw-semibold">{sessionLabel}</h6>
              <p className="guides-page-meta text-truncate">
                {browse ? setData.title : `${filter}${shuffled ? ' · shuffled' : ''}`}
              </p>
            </div>
            {browse && (
              <div className="guides-list-actions">
                <div className="radio-inputs" style={{ width: 'fit-content' }}>
                  <label className="radio">
                    <input
                      type="radio"
                      name="fc-browse-layout"
                      checked={layout === 'cards'}
                      onChange={() => setLayout('cards')}
                      aria-label="Card view"
                    />
                    <span className="name px-md-3 p-2">
                      <i className="bi bi-card-text me-1" />
                      <span className="d-none d-md-inline">Cards</span>
                    </span>
                  </label>
                  <label className="radio">
                    <input
                      type="radio"
                      name="fc-browse-layout"
                      checked={layout === 'list'}
                      onChange={() => setLayout('list')}
                      aria-label="List view"
                    />
                    <span className="name px-md-3 p-2">
                      <i className="bi bi-list-ul me-1" />
                      <span className="d-none d-md-inline">List</span>
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        {browse && layout === 'list' ? (
          <div className="fc-browse-list-wrap">
            <div className="fc-browse-head">
              <span className="fc-browse-col-num">#</span>
              <span>Term</span>
              <span>Definition</span>
              <button
                type="button"
                className={`fc-browse-reveal-all${allRevealed ? ' is-on' : ''}`}
                onClick={toggleRevealAll}
              >
                <i className={`bi ${allRevealed ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden />
                <span>{allRevealed ? 'Hide all' : 'Reveal all'}</span>
              </button>
            </div>
            <div className="fc-browse-list">
              {queue.map((card, i) => {
                const revealed = revealedIds.has(card.id);
                return (
                  <div key={card.id} className={`fc-browse-row${revealed ? ' is-revealed' : ''}`}>
                    <span className="fc-browse-num">{i + 1}</span>
                    <div
                      className="fc-browse-term eval-rich-content"
                      dangerouslySetInnerHTML={{ __html: card.frontHtml || '<p class="text-muted">Empty</p>' }}
                    />
                    <div
                      className={`fc-browse-def eval-rich-content${revealed ? '' : ' is-hidden'}`}
                      aria-hidden={!revealed}
                    >
                      <div dangerouslySetInnerHTML={{ __html: card.backHtml || '<p class="text-muted">Empty</p>' }} />
                    </div>
                    <button
                      type="button"
                      className={`fc-browse-reveal${revealed ? ' is-on' : ''}`}
                      aria-label={revealed ? `Hide definition ${i + 1}` : `Reveal definition ${i + 1}`}
                      aria-pressed={revealed}
                      onClick={() => toggleReveal(card.id)}
                    >
                      <i className={`bi ${revealed ? 'bi-eye-slash' : 'bi-eye'}`} aria-hidden />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
        <div className={`fc-study-stage${browse ? ' is-browse' : ''}`}>
          <div className="fc-study-progress-block">
            <div className="fc-study-progress-head">
              <span className="fc-study-progress-count ms-auto">{progressLabel}</span>
            </div>
            <div
              className="fc-study-segments"
              role="list"
              aria-label={`Progress: ${progressLabel}`}
            >
              {segmentStates.map((state, i) => (
                <span
                  key={queue[i]?.id ?? i}
                  role="listitem"
                  className={`fc-study-segment is-${state}`}
                  title={
                    state === 'current'
                      ? 'Current card'
                      : state === 'pos'
                        ? positiveLabel
                        : state === 'neg'
                          ? negativeLabel
                          : 'Not answered'
                  }
                  aria-label={
                    state === 'current'
                      ? `Card ${i + 1}, current`
                      : state === 'pos'
                        ? `Card ${i + 1}, ${positiveLabel}`
                        : state === 'neg'
                          ? `Card ${i + 1}, ${negativeLabel}`
                          : `Card ${i + 1}, not answered`
                  }
                />
              ))}
            </div>
          </div>

          <button
            type="button"
            className={`fc-study-card${flipped ? ' is-flipped' : ''}`}
            onClick={() => setFlipped((f) => !f)}
            aria-label={flipped ? 'Show front' : 'Flip to reveal back'}
          >
            <div className="fc-study-card-inner">
              <div className="fc-study-card-face fc-study-card-front shadow-sm">
                <div className="fc-study-card-label">Front</div>
                <div
                  className="fc-study-card-content eval-rich-content"
                  dangerouslySetInnerHTML={{ __html: current?.frontHtml || '<p class="text-muted">Empty</p>' }}
                />
                <div className="fc-study-card-hint">Tap to flip</div>
              </div>
              <div className="fc-study-card-face fc-study-card-back">
                <div className="fc-study-card-label">Back</div>
                <div
                  className="fc-study-card-content eval-rich-content"
                  dangerouslySetInnerHTML={{ __html: current?.backHtml || '<p class="text-muted">Empty</p>' }}
                />
                <div className="fc-study-card-hint">Tap to flip</div>
              </div>
            </div>
          </button>

          <div className={`fc-study-toolbar${flipped && !browse ? ' is-revealed' : ''}${browse ? ' is-browse' : ''}`}>
            {!browse && (
            <button
              type="button"
              className={`fc-study-mark fc-study-mark-neg${currentResult === negativeResult ? ' is-active' : ''}`}
              tabIndex={flipped ? 0 : -1}
              aria-hidden={!flipped}
              onClick={() => markAndAdvance(negativeResult)}
            >
              {negativeLabel}
            </button>
            )}

            <div className="fc-study-nav">
              <button
                type="button"
                className="fc-study-nav-btn"
                disabled={index <= 0}
                aria-label="Previous card"
                onClick={goPrev}
              >
                <i className="bi bi-chevron-left" aria-hidden />
              </button>
              <div className="fc-study-progress">{progressLabel}</div>
              <button
                type="button"
                className="fc-study-nav-btn"
                disabled={browse && index + 1 >= queue.length}
                aria-label={browse ? 'Next card' : index + 1 >= queue.length ? 'Finish session' : 'Next card'}
                onClick={goNext}
              >
                <i className="bi bi-chevron-right" aria-hidden />
              </button>
            </div>

            {!browse && (
            <button
              type="button"
              className={`fc-study-mark fc-study-mark-pos${currentResult === positiveResult ? ' is-active' : ''}`}
              tabIndex={flipped ? 0 : -1}
              aria-hidden={!flipped}
              onClick={() => markAndAdvance(positiveResult)}
            >
              {positiveLabel}
            </button>
            )}
          </div>
        </div>
        )}
        </div>
      </Container>
    </div>
  );
}
